import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Star, ShieldCheck, Clock, ThumbsUp } from "lucide-react";
import { useSiteContent, useServices, useGallery, useTestimonials } from "@/lib/hooks";
import ServiceIcon from "@/components/ServiceIcon";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { fileUrl } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1711098256574-7b497260cdc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMHN0b25lJTIwcGF0aW8lMjBnYXJkZW4lMjBsdXh1cnl8ZW58MHx8fHwxNzg1NDc5NDU3fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { data: content } = useSiteContent();
  const { data: services = [] } = useServices();
  const { data: featured = [] } = useGallery(null, true);
  const { data: testimonials = [] } = useTestimonials();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center">
        <img src={HERO_IMG} alt="Clean patio" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-green-600/90 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white mb-6">
              <Star className="h-3.5 w-3.5 fill-white" /> {content?.tagline || "The Outdoor Hygiene Doctor"}
            </span>
            <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {content?.hero_text || "Professional outdoor cleaning & garden care that makes your property shine."}
            </h1>
            <p className="mt-6 text-lg text-slate-100/90 max-w-xl leading-relaxed">
              {content?.strapline || "Restoring the sparkle to your patios, drives & gardens."}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/quote" data-testid="hero-quote-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-green-600 px-8 py-4 text-base font-bold text-white hover:bg-green-700 transition-colors shadow-lg">
                Get a Free Quote <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/gallery" data-testid="hero-gallery-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/95 px-8 py-4 text-base font-bold text-slate-900 hover:bg-white transition-colors">
                See Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, label: "Fully Insured" },
            { icon: ThumbsUp, label: "Satisfaction Guaranteed" },
            { icon: Clock, label: "Punctual & Reliable" },
            { icon: Star, label: "5-Star Rated Locally" },
          ].map((t, i) => (
            <div key={i} className="flex items-center gap-3">
              <t.icon className="h-7 w-7 text-green-500 shrink-0" />
              <span className="text-sm font-semibold">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES SNAPSHOT */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-24">
        <div className="max-w-2xl">
          <span className="text-green-700 font-bold uppercase tracking-widest text-xs">What we do</span>
          <h2 className="font-display font-extrabold text-slate-900 text-3xl sm:text-4xl mt-3 tracking-tight">Outdoor services built around your home</h2>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.slice(0, 6).map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="group rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
              data-testid={`home-service-card-${i}`}
            >
              <div className="h-12 w-12 rounded-xl bg-green-50 text-green-600 grid place-items-center group-hover:bg-green-600 group-hover:text-white transition-colors">
                <ServiceIcon name={s.icon} />
              </div>
              <h3 className="font-display font-bold text-slate-900 text-lg mt-5">{s.name}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed line-clamp-3">{s.description}</p>
              {s.starting_price && <p className="mt-4 text-sm font-bold text-green-700">{s.starting_price}</p>}
            </motion.div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/services" className="inline-flex items-center gap-2 font-bold text-slate-900 hover:text-green-700" data-testid="home-all-services">
            View all services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* BEFORE / AFTER PREVIEW */}
      {featured.length > 0 && (
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-24">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div className="max-w-xl">
                <span className="text-green-700 font-bold uppercase tracking-widest text-xs">The proof</span>
                <h2 className="font-display font-extrabold text-slate-900 text-3xl sm:text-4xl mt-3 tracking-tight">See the transformation</h2>
                <p className="text-slate-500 mt-3">Drag the slider to reveal the difference our team makes.</p>
              </div>
              <Link to="/gallery" className="inline-flex items-center gap-2 font-bold text-slate-900 hover:text-green-700 shrink-0" data-testid="home-full-gallery">
                Full gallery <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {featured.slice(0, 3).map((item) => (
                <div key={item.id} className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
                  <BeforeAfterSlider before={item.before_image_url} after={item.after_image_url} className="aspect-[4/3]" />
                  <div className="p-4">
                    <p className="font-semibold text-slate-900 text-sm">{item.caption}</p>
                    <p className="text-xs text-green-700 font-semibold mt-1">{item.service_category}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-24">
          <h2 className="font-display font-extrabold text-slate-900 text-3xl sm:text-4xl tracking-tight text-center max-w-xl mx-auto">Homeowners love the results</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {testimonials.slice(0, 3).map((t) => (
              <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm" data-testid="home-testimonial">
                <div className="flex gap-1 text-green-500 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-green-500" />)}
                </div>
                <p className="text-slate-700 leading-relaxed">"{t.quote_text}"</p>
                <p className="mt-5 font-bold text-slate-900 text-sm">{t.author_name}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-green-600">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 md:py-20 text-center">
          <h2 className="font-display font-black text-white text-3xl sm:text-4xl tracking-tight">Ready for a cleaner outdoors?</h2>
          <p className="text-green-50 mt-4 text-lg max-w-xl mx-auto">Tell us what you need and we'll get back to you with a free, no-obligation quote.</p>
          <Link to="/quote" data-testid="cta-quote-btn" className="inline-flex items-center gap-2 mt-8 rounded-full bg-white px-8 py-4 text-base font-bold text-green-700 hover:bg-green-50 transition-colors shadow-lg">
            Get a Free Quote <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
